import React, { useEffect, useState } from 'react';
import './tooltip.css';
import { store } from '../../../app/store';
import { postAPI } from '../../../app/api';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { hasPermission, isAdmin } from '../../../store/hooks/Permission';

export default function List({ PopupID = '', setIsEdit, setEditId }) {
  const store_data = JSON.parse(store());
  const [queries, setQueries] = useState([]);
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [replyForm, setReplyForm] = useState({ subject: '', reply_message: '' });
  const [isReplying, setIsReplying] = useState(false);

  const [canView, setCanView] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);

  const fetchQueries = async () => {
    const response = await postAPI('/queries/list', { org_id: store_data?.org_id, user: store_data });

    if (response?.status === 1) {
      setQueries(response?.data || []);
    } else {
      setQueries([]);
    }
  };

  useEffect(() => {
    const checkPermissions = async () => {
      if (isAdmin()) {
        setCanView(true);
        setCanEdit(true);
        setCanDelete(true);
        fetchQueries();
      } else {
        const viewAllowed = await hasPermission('QUERIES', 'Queries', 'VIEW');
        const editAllowed = await hasPermission('QUERIES', 'Queries', 'EDIT');
        const deleteAllowed = await hasPermission('QUERIES', 'Queries', 'DELETE');

        setCanView(viewAllowed);
        setCanEdit(editAllowed);
        setCanDelete(deleteAllowed);

        if (viewAllowed) {
          fetchQueries();
        }
      }
    };

    checkPermissions();
  }, []);

  const handleRemove = async (id) => {
    if (!canDelete) {
      toast.error('You do not have permission to perform this action');
      return;
    }

    const response = await postAPI('/queries/remove', { org_id: store_data?.org_id, id, user: store_data });

    if (response?.status === 1) {
      toast.success('Query removed successfully.');
      window.location.reload();
    } else {
      toast.error(response?.error || 'Sorry! Something went wrong.');
    }
  };

  const openReplyModal = (query) => {
    const hasEmail = !!(query?.email && String(query.email).trim());
    setSelectedQuery(query);
    setReplyForm({
      subject: hasEmail ? `Reply to your query from Stremax Scholar` : '',
      reply_message: '',
    });
  };

  const closeReplyModal = () => {
    setSelectedQuery(null);
    setReplyForm({ subject: '', reply_message: '' });
    setIsReplying(false);
  };

  const handleReply = async () => {
    if (!selectedQuery?.id || isReplying) return;

    if (!replyForm.reply_message?.trim()) {
      toast.error('Reply message is required.');
      return;
    }

    setIsReplying(true);

    const response = await postAPI('/queries/reply', {
      id: selectedQuery.id,
      org_id: store_data?.org_id,
      subject: replyForm.subject,
      reply_message: replyForm.reply_message,
      user: store_data,
    });

    if (response?.status === 1) {
      toast.success(selectedQuery?.email ? 'Reply sent successfully.' : 'Query marked as replied successfully.');
      closeReplyModal();
      window.location.reload();
    } else {
      toast.error(response?.error || 'Sorry! Something went wrong.');
      setIsReplying(false);
    }
  };

  return (
    <>
      <ToastContainer />
      <table className="table card-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Contact Number</th>
            <th>Address</th>
            <th>Message</th>
            <th>Reply Status</th>
            <th style={{ width: '220px' }}>Actions</th>
          </tr>
        </thead>
        <tbody className="table-border-bottom-0">
          {!canView ? (
            <tr>
              <td colSpan="7" className="text-center text-danger py-4">
                You do not have permission to view this page.
              </td>
            </tr>
          ) : queries.length > 0 ? (
            queries.map((val, index) => (
              <tr key={val?.id || index}>
                <td>{val?.subscriber_name || '-'}</td>
                <td>{val?.email || '-'}</td>
                <td>{val?.contact_number || '-'}</td>
                <td>{val?.address || '-'}</td>
                <td style={{ maxWidth: '280px', whiteSpace: 'pre-wrap' }}>{val?.message || '-'}</td>
                <td>
                  {val?.is_replied === 1 ? (
                    <span className="badge bg-label-success">{val?.reply_mode === 'EMAIL' ? 'Replied by Email' : 'Marked Replied'}</span>
                  ) : val?.reply_status === 'FAILED' ? (
                    <span className="badge bg-label-danger">Reply Failed</span>
                  ) : (
                    <span className="badge bg-label-warning">Pending</span>
                  )}
                </td>
                <td>
                  {canEdit && (val?.email || val?.contact_number) && (
                    <span
                      className="badge bg-label-success me-1 custom-tooltip"
                      data-bs-toggle="modal"
                      data-bs-target="#QueryReplyModal"
                      onClick={() => openReplyModal(val)}
                    >
                      <i className="fa fa-reply"></i>
                      <span className="tooltip-text">{val?.email ? 'Reply via Email' : 'Mark as Replied'}</span>
                    </span>
                  )}

                  {canEdit && (
                    <span
                      className="badge bg-label-primary me-1 custom-tooltip"
                      data-bs-toggle="modal"
                      data-bs-target={`#${PopupID}`}
                      onClick={() => {
                        setIsEdit(true);
                        setEditId(val?.id);
                      }}
                    >
                      <i className="fa fa-pen"></i>
                      <span className="tooltip-text">Edit Query</span>
                    </span>
                  )}

                  {canDelete && (
                    <span
                      className="badge bg-label-danger me-1 cursor-pointer custom-tooltip"
                      onClick={() => {
                        if (window.confirm('Are you sure? This action will permanently remove this data!')) {
                          handleRemove(val?.id);
                        }
                      }}
                    >
                      <i className="fa fa-trash"></i>
                      <span className="tooltip-text">Delete Query</span>
                    </span>
                  )}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="6" className="text-center py-4">No queries found.</td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="modal fade" id="QueryReplyModal" tabIndex="-1" aria-hidden="true">
        <div className="modal-dialog modal-lg modal-simple modal-edit-user">
          <div className="modal-content p-3 p-md-5">
            <div className="modal-body">
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" onClick={closeReplyModal}></button>
              <div className="text-center mb-4">
                <h3 className="mb-2">{selectedQuery?.email ? 'Reply to Query' : 'Mark Query as Replied'}</h3>
                <p className="text-muted mb-0">
                  {selectedQuery?.email
                    ? `This reply will be sent to ${selectedQuery?.email} and logged.`
                    : 'This query has no email address. Add a reply note and mark it as replied.'}
                </p>
              </div>

              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <label className="form-label">Name</label>
                  <input className="form-control" value={selectedQuery?.subscriber_name || ''} disabled />
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label">Contact</label>
                  <input className="form-control" value={selectedQuery?.email || selectedQuery?.contact_number || ''} disabled />
                </div>

                {selectedQuery?.email && (
                  <div className="col-12">
                    <label className="form-label">Subject</label>
                    <input
                      className="form-control"
                      value={replyForm.subject}
                      onChange={(e) => setReplyForm({ ...replyForm, subject: e.target.value })}
                      placeholder="Enter email subject"
                    />
                  </div>
                )}

                <div className="col-12">
                  <label className="form-label">Reply Message</label>
                  <textarea
                    className="form-control"
                    rows="6"
                    value={replyForm.reply_message}
                    onChange={(e) => setReplyForm({ ...replyForm, reply_message: e.target.value })}
                    placeholder={selectedQuery?.email ? 'Enter your reply message' : 'Enter a note about how this query was replied to'}
                  ></textarea>
                </div>

                {selectedQuery?.reply_log && (
                  <div className="col-12">
                    <label className="form-label">Previous Reply Log</label>
                    <div className="border rounded p-3 bg-light" style={{ maxHeight: '220px', overflowY: 'auto', whiteSpace: 'pre-wrap' }}>
                      {(() => {
                        try {
                          const logs = JSON.parse(selectedQuery.reply_log);
                          return Array.isArray(logs) && logs.length > 0
                            ? logs.map((entry, idx) => (
                                <div key={idx} className="mb-3 pb-3 border-bottom">
                                  <div><strong>Status:</strong> {entry?.status || '-'}</div>
                                  <div><strong>Mode:</strong> {entry?.mode || '-'}</div>
                                  <div><strong>By:</strong> {entry?.replied_by || '-'}</div>
                                  <div><strong>At:</strong> {entry?.replied_at || '-'}</div>
                                  {entry?.subject ? <div><strong>Subject:</strong> {entry.subject}</div> : ''}
                                  <div><strong>Message:</strong> {entry?.message || '-'}</div>
                                </div>
                              ))
                            : 'No previous reply log found.';
                        } catch (error) {
                          return 'Unable to read reply log.';
                        }
                      })()}
                    </div>
                  </div>
                )}

                <div className="col-12 text-center mt-3">
                  <button type="button" className="btn btn-primary me-2" onClick={handleReply} disabled={isReplying}>
                    {isReplying ? 'Processing...' : selectedQuery?.email ? 'Send Reply' : 'Mark as Replied'}
                  </button>
                  <button type="button" className="btn btn-label-secondary" data-bs-dismiss="modal" onClick={closeReplyModal}>Cancel</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
